import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useResolvedTenant } from '../hooks/useResolvedTenant.js';

/**
 * Project breadcrumb selector (card 050).
 *
 * Sits to the right of TenantPicker.  Shows the active project's
 * name (resolved from the URL's :slug, if any) as a chip; click
 * opens a searchable list of projects in the active tenant.
 *
 * Scope guarantee:
 *
 *   Pulls from `/v1/admin/projects` with the active tenant header
 *   set by `createApiClient`.  We never enumerate cross-tenant
 *   projects client-side — the request itself is tenant-scoped on
 *   the server.  Selection cache is keyed by `(platform_user_id,
 *   tenant_id)` so switching tenant drops the previous list and we
 *   refetch.  See `useTenantProjects` for the keying.
 *
 * Behaviour:
 *
 *   - LOADING: skeleton chip.
 *   - EMPTY:   popover surfaces a nudge to Tenant admin → Projects
 *              (the canonical create-project page) — we don't
 *              duplicate the create form here because project
 *              create involves a wider config (default site, theme,
 *              copy of a starter tour, etc.) that belongs on the
 *              full Projects route.
 *   - LOADED:  list with search once the count exceeds ~5.  Click
 *              navigates to `/projects/:slug` which deep-routes to
 *              the project overview.
 */
export function ProjectPicker({ activeSlug }) {
  const navigate = useNavigate();
  const { projects, loading, reload } = useTenantProjects();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  // Refetch when the popover re-opens so stale data from a long
  // session is corrected without forcing a full page reload.  Cheap:
  // /v1/admin/projects responds in a few ms once authed.
  useEffect(() => {
    if (open) reload();
    // We intentionally exclude `reload` from deps to avoid re-running
    // the reload-loop fetch on every open transition; reload itself
    // is stable from the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus?.();
      }
    };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (projects.length > 5) {
      const t = window.setTimeout(() => searchRef.current?.focus?.(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, projects.length]);

  const active = useMemo(
    () => (activeSlug ? projects.find((p) => p.slug === activeSlug) : null),
    [projects, activeSlug],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const haystack = [p.name, p.slug].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [projects, query]);

  function selectProject(slug) {
    setOpen(false);
    navigate(`/projects/${slug}`);
  }

  // Loading skeleton on first paint.
  if (loading && projects.length === 0) {
    return (
      <div className="hb-picker hb-picker--loading">
        <span className="hb-picker__sep" aria-hidden="true">/</span>
        <span className="hb-picker__skeleton" aria-hidden="true" />
        <span className="sr-only">Loading projects…</span>
      </div>
    );
  }

  // No projects in the tenant — collapse the breadcrumb segment to
  // a single chip with a friendly empty state.  Tenant picker is
  // still useful in this case.
  const triggerLabel = active?.name
    || (activeSlug ? activeSlug : (projects.length === 0 ? 'No projects yet' : 'All projects'));

  return (
    <div className="hb-picker hb-picker--with-sep" ref={rootRef}>
      <span className="hb-picker__sep" aria-hidden="true">/</span>
      <button
        ref={triggerRef}
        type="button"
        data-ui-name="project-picker"
        className={`hb-picker__trigger${open ? ' hb-picker__trigger--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ProjectIcon />
        <span className="hb-picker__label">{triggerLabel}</span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div className="hb-picker__popover" role="menu" aria-label="Projects">
          {projects.length > 5 && (
            <div className="hb-picker__search">
              <SearchIcon />
              <input
                ref={searchRef}
                type="search"
                className="hb-picker__search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search projects…"
                aria-label="Search projects"
              />
            </div>
          )}

          {loading ? (
            <div className="hb-picker__empty hb-picker__empty--loading">
              Loading projects…
            </div>
          ) : projects.length === 0 ? (
            <div className="hb-picker__empty">
              <strong>No projects yet.</strong>
              <p>
                Create your first project to start shipping tours,
                content and themes.
              </p>
              <button
                type="button"
                className="btn btn--primary"
                style={{ marginTop: 8 }}
                onClick={() => { setOpen(false); navigate('/projects?create=1'); }}
              >
                Create project
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="hb-picker__empty">
              No projects match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <ul className="hb-picker__list">
              {filtered.map((p) => (
                <li key={p.project_id || p.slug}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={p.slug === activeSlug}
                    className={`hb-picker__item${
                      p.slug === activeSlug ? ' hb-picker__item--active' : ''
                    }`}
                    onClick={() => selectProject(p.slug)}
                  >
                    <span className="hb-picker__item-main">
                      <span className="hb-picker__item-name">{p.name || p.slug}</span>
                      {p.slug && p.slug !== p.name && (
                        <span className="hb-picker__item-slug">{p.slug}</span>
                      )}
                    </span>
                    {p.slug === activeSlug && <CheckIcon />}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {projects.length > 0 && (
            <>
              <div className="hb-picker__divider" role="separator" />
              <button
                type="button"
                className="hb-picker__action"
                onClick={() => { setOpen(false); navigate('/projects'); }}
              >
                <FolderIcon />
                <span>Manage projects…</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Hook: load the active tenant's projects.
 *
 * Cache is keyed on the active tenant so switching tenants drops
 * the previous list immediately — the picker never shows projects
 * from a tenant the user just left.  No localStorage; this is a
 * per-mount fetch with a manual reload hook.
 */
export function useTenantProjects() {
  const { getToken } = useAuth();
  // Card 097 — gate the GET /v1/admin/projects call on a resolved
  // tenant (one the user is a member of).  Before this, the fallback
  // DEMO_TENANT_UUID was always truthy and the picker hit /admin/
  // before any tenant existed → 404 swarm on every fresh sign-in.
  const { tenantId, isResolved } = useResolvedTenant();
  const api = useMemo(
    () => (tenantId ? createApiClient(getToken, { tenantId }) : null),
    [getToken, tenantId],
  );
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!api || !isResolved) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api('/v1/admin/projects');
      const list = Array.isArray(data) ? data : (data?.items || []);
      // Stable order: by name, with slug as tiebreaker.
      list.sort((a, b) => {
        const an = (a.name || a.slug || '').toLowerCase();
        const bn = (b.name || b.slug || '').toLowerCase();
        if (an !== bn) return an < bn ? -1 : 1;
        return (a.slug || '').localeCompare(b.slug || '');
      });
      setProjects(list);
    } catch {
      // Network/auth errors are non-fatal — the picker shows the
      // empty state.  The pages that need projects will surface
      // their own errors.
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [api, isResolved]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await load();
      if (!alive) return;
    })();
    return () => { alive = false; };
  }, [load]);

  return { projects, loading, reload: load };
}

// ───────── icons (lucide-style, inline)

function ProjectIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9L8.93 3.9a2 2 0 0 0-1.66-.9H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9L8.93 3.9a2 2 0 0 0-1.66-.9H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}
